import React from 'react'

import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import { DayType } from '../../../schedule/constants/DayType'
import { getDayTypeName } from '../../../schedule/helpers/TimesheetDayType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDayEvents } from '@/modules/drivers/hooks/useDayEvents'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import ScheduleContextMenuSearchBar from '../../../schedule/contextMenu/ScheduleContextMenuSearchBar'
import ScheduleContextMenuOption from '../../../schedule/contextMenu/ScheduleContextMenuOption'
import DayTypesModal from '../../../schedule/modal/DayTypesModal'
import {
  FaIcon,
  faRotateReverseSolid,
  faArrowUpRightFromSquareSolid,
  faMagnifyingGlassMinusSolid,
} from '@/lib/fa'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useTimesheet } from '../../context/useTimesheet'

type TimesheetDayTypePanelProps = {
  companyId: string
  driverId: string
  onClose: () => void
  onDayTypeChanged?: () => void
  isPinnedLoading: boolean
  pinnedDays: DayType[]
  fetchPinnedDays: () => void
}

const TimesheetDayTypePanel: React.FC<TimesheetDayTypePanelProps> = ({
  companyId,
  driverId,
  onClose,
  onDayTypeChanged,
  isPinnedLoading,
  pinnedDays,
  fetchPinnedDays,
}) => {
  const {
    selectedDays,
    isOpenDayTypeModal,
    setIsOpenDayTypeModal,
    setIsLoading,
    setSelectedDays,
    isLoading,
  } = useTimesheet()

  const { handleRestoreDefaultDayType } = useDayEvents()
  const { changeDayTypes } = useTimesheetService()
  const { changeDayTypes: changeDayDetailsCalendar } = useCalendarService()
  const { t } = useLocalize()

  const [searchQuery, setSearchQuery] = React.useState('')

  React.useEffect(() => {
    fetchPinnedDays()
  }, [isOpenDayTypeModal])

  const changeDayType = async (type: number) => {
    setIsLoading(true)
    try {
      await changeDayTypes({
        companyId,
        driverId: Number(driverId),
        dates: selectedDays,
        dayType: type,
        notes: null,
      })
    } catch (error) {
      console.error('Error changing day type:', error)
    } finally {
      setIsLoading(false)
      setSelectedDays([])
      onClose()
      onDayTypeChanged?.()
    }
  }

  const restoreDefaultDayData = async () => {
    setIsLoading(true)
    try {
      await handleRestoreDefaultDayType(
        companyId,
        driverId,
        selectedDays,
        changeDayDetailsCalendar,
      )
    } catch (error) {
      console.error('Error restoring default day type:', error)
    } finally {
      setIsLoading(false)
      setSelectedDays([])
      onClose()
      onDayTypeChanged?.()
    }
  }

  const allDayTypes = Object.values(DayType)
    .filter((value) => typeof value === 'number' && value !== DayType.NONE)
    .map((value) => value as DayType)

  const filteredDayTypes = React.useMemo(() => {
    if (!searchQuery) return pinnedDays

    return allDayTypes.filter((dayType) => {
      const dayTypeName = getDayTypeName(dayType, t).toLowerCase()
      const dayTypeCode = DayType[dayType].toLowerCase()
      const query = searchQuery.toLowerCase()
      return dayTypeName.includes(query) || dayTypeCode.includes(query)
    })
  }, [searchQuery, allDayTypes, pinnedDays, t])

  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-z-[9998]"
        onClick={(e) => {
          e.stopPropagation()
          if (!isOpenDayTypeModal) onClose()
        }}
      />

      <div
        className="tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[9999] tw-max-h-[500px] tw-min-h-[500px] tw--translate-x-1/2 tw--translate-y-1/2"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading && (
          <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-grid tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}
        <div className="tw-flex tw-max-h-[500px] tw-min-h-[500px] tw-w-[380px] tw-flex-col tw-space-y-1 tw-rounded-lg tw-bg-white tw-p-2 tw-px-1.5 tw-shadow-lg">
          <ScheduleContextMenuSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          {!searchQuery && (
            <span className="tw-mb-1.5 tw-mt-1 tw-px-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base">
              Przypięte
            </span>
          )}
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-pr-1">
            {isPinnedLoading && !searchQuery ? (
              <div className="tw-flex tw-h-[300px] tw-items-center tw-justify-center">
                <LoaderIcon className="tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
              </div>
            ) : filteredDayTypes.length > 0 ? (
              filteredDayTypes.map((dayType) => (
                <ScheduleContextMenuOption
                  key={dayType}
                  dayType={dayType}
                  changeDayType={changeDayType}
                  companyId={companyId}
                  searchQuery={searchQuery}
                  isPinned={pinnedDays.includes(dayType)}
                  onPinToggle={fetchPinnedDays}
                />
              ))
            ) : (
              <div className="tw-flex tw-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-gap-4">
                <FaIcon
                  icon={faMagnifyingGlassMinusSolid}
                  className="tw-h-10 tw-w-10 tw-text-[#D1E2F6]"
                />
                <span className="tw-text-xs tw-text-content-base/80">
                  {!searchQuery
                    ? 'Brak przypiętych typów dni'
                    : 'Nie znaleziono typów dni'}
                </span>
              </div>
            )}
          </div>
          {!searchQuery && (
            <>
              <DividerHorizontal />
              <div
                onClick={() => restoreDefaultDayData()}
                className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-py-1 hover:tw-bg-[#F1F4FF]"
              >
                <FaIcon
                  icon={faRotateReverseSolid}
                  className="tw-h-3 tw-w-3 tw-text-primary"
                />
                <span className="tw-text-sm tw-font-normal tw-text-primary">
                  Przywróć domyślny typ dnia
                </span>
              </div>
              <DividerHorizontal />
              <div
                onClick={() => setIsOpenDayTypeModal(true)}
                className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-py-1 hover:tw-bg-[#F1F4FF]"
              >
                <span className="tw-text-sm tw-font-normal tw-text-content-base">
                  Wszystkie typy dnia
                </span>
                <FaIcon
                  icon={faArrowUpRightFromSquareSolid}
                  className="tw-h-3 tw-w-3 tw-text-content-base"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {isOpenDayTypeModal && (
        <DayTypesModal
          close={() => setIsOpenDayTypeModal(false)}
          onSubmit={() => {
            setSelectedDays([])
            onClose()
            onDayTypeChanged?.()
          }}
          companyId={companyId}
          driverId={driverId}
        />
      )}
    </>
  )
}

export default TimesheetDayTypePanel
